// Generic label + control + optional hint/error wrapper used across forms.
//
// WHY: a per-row `useId()` is published via `FormRowContext` so the nested
// form control (Input, PasswordField, Select, PhoneInput) can opt in via
// `useFormRowId()` and expose an `id` that matches the `<label htmlFor>`.
// This gives the input a clean accessible name (just the label text)
// without pulling sibling button or hint content into the name, which
// otherwise causes `getByLabelText` to fuzz-match other controls in the
// same modal.
//
// WHY (#3039): the row additionally publishes the ids of its hint/error
// nodes and whether it is currently in error, so a nested control can wire
// `aria-describedby` / `aria-invalid` without every feature form re-plumbing
// ids by hand. Every id derives from this row's own `useId()`, so two rows
// in one form can never cross-link their errors.
//
// INVARIANT: `FormRowContext` is module-private — only `useFormRowId`,
// `useFormRowControl` and `FormRow` are exported, and only `useFormRowId` +
// `FormRow` reach the UI barrel. Consumers must not subscribe to the context
// directly.
import { createContext, useContext, useId, useMemo, type ReactNode } from 'react';

interface FormRowState {
  /** Control `id`, matching the row `<label htmlFor>`. */
  id: string;
  /** Space-separated ids of the row's hint and error nodes, in reading order. */
  describedBy: string | undefined;
  /** True while the row renders an error message. */
  invalid: boolean;
}

const FormRowContext = createContext<FormRowState | null>(null);

export function useFormRowId(): string | undefined {
  return useContext(FormRowContext)?.id;
}

export interface FormRowControlOverrides {
  /** Explicit control id; wins over the row's generated id. */
  id?: string | undefined;
  /** Caller-owned description ids; merged with — never replaced by — the row's. */
  describedBy?: string | undefined;
  /** Explicit validity; wins over the row's error state (including `false`). */
  invalid?: boolean | undefined;
}

/**
 * @aiPurpose resolve the a11y attributes a `ui` form control renders, from
 *   its own props merged with the enclosing {@link FormRow}.
 * @aiInvariant caller-supplied values are never dropped: an explicit `id` /
 *   `invalid` overrides the row, while an explicit `describedBy` is kept and
 *   the row's hint/error ids are appended after it.
 */
export function useFormRowControl(overrides: FormRowControlOverrides = {}): {
  id: string | undefined;
  describedBy: string | undefined;
  invalid: boolean;
} {
  const row = useContext(FormRowContext);
  const describedBy = [overrides.describedBy, row?.describedBy]
    .filter((part): part is string => !!part)
    .join(' ');
  return {
    id: overrides.id ?? row?.id,
    describedBy: describedBy === '' ? undefined : describedBy,
    invalid: overrides.invalid ?? row?.invalid ?? false,
  };
}

export function FormRow({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  /** Static guidance shown under the label and linked as a description. */
  hint?: string | undefined;
  error?: string | undefined;
  children: ReactNode;
}) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const state = useMemo<FormRowState>(() => {
    const described = [hint ? hintId : null, error ? errorId : null].filter(
      (part): part is string => part !== null,
    );
    return {
      id,
      describedBy: described.length === 0 ? undefined : described.join(' '),
      invalid: !!error,
    };
  }, [id, hintId, errorId, hint, error]);
  return (
    <FormRowContext.Provider value={state}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 10 }}>
        <label htmlFor={id} style={{ fontSize: 12, color: 'var(--fg-2)' }}>
          {label}
        </label>
        {hint ? (
          <span id={hintId} style={{ fontSize: 'var(--fz-11)', color: 'var(--fg-3)' }}>
            {hint}
          </span>
        ) : null}
        {children}
        {error ? (
          <span id={errorId} role="alert" style={{ fontSize: 11, color: 'var(--status-error-fg)' }}>
            {error}
          </span>
        ) : null}
      </div>
    </FormRowContext.Provider>
  );
}
